import React, { useEffect, useMemo, useState } from "react";
import { getClassStudents, getSubjectDetails } from "../../../redux/sclassRelated/sclassHandle";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    Alert,
    alpha,
    Box,
    Button,
    Chip,
    Container,
    Divider,
    Fade,
    Grid,
    IconButton,
    Paper,
    Skeleton,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from "@mui/material";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";

import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import GradingOutlinedIcon from "@mui/icons-material/GradingOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";

const ViewSubject = () => {
    const navigate = useNavigate();
    const params = useParams();
    const dispatch = useDispatch();
    const { subloading, subjectDetails, sclassStudents, getresponse, error } = useSelector((state) => state.sclass);

    const { classID, subjectID } = params;

    const [mainTab, setMainTab] = useState("1");
    const [studentMode, setStudentMode] = useState("attendance");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        dispatch(getSubjectDetails(subjectID, "Subject"));
        dispatch(getClassStudents(classID));
    }, [dispatch, subjectID, classID]);

    useEffect(() => {
        setPage(0);
    }, [studentMode, sclassStudents]);

    const students = useMemo(() => (Array.isArray(sclassStudents) ? sclassStudents : []), [sclassStudents]);

    const studentRows = useMemo(
        () =>
            students.map((student) => ({
                rollNum: student.rollNum,
                name: student.name,
                id: student._id,
            })),
        [students]
    );

    const paginatedRows = useMemo(
        () => studentRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [studentRows, page, rowsPerPage]
    );

    const elective = Boolean(subjectDetails?.isElective);
    const mie = subjectDetails?.mieSyllabusCode?.trim();
    const classLabel = subjectDetails?.sclassName?.sclassName || "—";
    const subName = subjectDetails?.subName || "Subject";
    const teacher = subjectDetails?.teacher;

    const handleMainTab = (_, v) => setMainTab(v);

    return (
        <Box
            component="main"
            sx={{
                minHeight: "100%",
                pb: 4,
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.secondary.main, 0.07)} 0%, ${t.palette.grey[50]} 28%)`
                        : alpha(t.palette.common.black, 0.2),
            }}
        >
            <Container maxWidth="lg" sx={{ pt: { xs: 2, sm: 3 } }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                        {error?.message || "Something went wrong loading this subject."}
                    </Alert>
                )}

                {subloading ? (
                    <Stack spacing={2}>
                        <Skeleton variant="rounded" height={140} sx={{ borderRadius: 3 }} />
                        <Skeleton variant="rounded" height={48} sx={{ borderRadius: 2 }} />
                        <Skeleton variant="rounded" height={220} sx={{ borderRadius: 2 }} />
                    </Stack>
                ) : (
                    <Fade in timeout={280}>
                        <Stack spacing={2.5}>
                            <Button
                                startIcon={<ArrowBackIosNewOutlinedIcon sx={{ fontSize: 14 }} />}
                                onClick={() =>
                                    navigate(
                                        classID
                                            ? `/Admin/subjects/class/${classID}`
                                            : "/Admin/subjects/chooseclass",
                                    )
                                }
                                sx={{ alignSelf: "flex-start", fontWeight: 600 }}
                            >
                                Back to subjects
                            </Button>

                            <Paper
                                elevation={0}
                                sx={{
                                    p: { xs: 2.5, sm: 3 },
                                    borderRadius: 3,
                                    color: "common.white",
                                    background: (t) =>
                                        `linear-gradient(120deg, ${t.palette.secondary.dark} 0%, ${t.palette.secondary.main} 50%, ${alpha(
                                            t.palette.secondary.light,
                                            0.92
                                        )} 100%)`,
                                    boxShadow: (t) => `0 14px 44px ${alpha(t.palette.secondary.main, 0.28)}`,
                                }}
                            >
                                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "flex-start" }}>
                                    <Box
                                        sx={{
                                            width: 56,
                                            height: 56,
                                            borderRadius: 2,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            bgcolor: alpha("#fff", 0.15),
                                            flexShrink: 0,
                                        }}
                                    >
                                        <MenuBookOutlinedIcon sx={{ fontSize: 32 }} />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="overline" sx={{ opacity: 0.92, letterSpacing: 1.1 }}>
                                            {classLabel}
                                        </Typography>
                                        <Typography variant="h4" component="h1" fontWeight={800} sx={{ lineHeight: 1.15 }} noWrap title={subName}>
                                            {subName}
                                        </Typography>
                                        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1.5 }}>
                                            <Chip
                                                label={elective ? "Elective" : "Compulsory"}
                                                sx={{
                                                    bgcolor: alpha("#fff", 0.2),
                                                    color: "inherit",
                                                    fontWeight: 600,
                                                    border: "1px solid",
                                                    borderColor: alpha("#fff", 0.45),
                                                }}
                                            />
                                            {mie ? (
                                                <Chip
                                                    label={`MIE ${mie}`}
                                                    sx={{
                                                        bgcolor: alpha("#fff", 0.2),
                                                        color: "inherit",
                                                        fontWeight: 600,
                                                        border: "1px solid",
                                                        borderColor: alpha("#fff", 0.45),
                                                    }}
                                                />
                                            ) : null}
                                            <Chip
                                                label={`${students.length} student${students.length === 1 ? "" : "s"}`}
                                                sx={{
                                                    bgcolor: alpha("#fff", 0.2),
                                                    color: "inherit",
                                                    fontWeight: 600,
                                                    border: "1px solid",
                                                    borderColor: alpha("#fff", 0.45),
                                                }}
                                            />
                                        </Stack>
                                    </Box>
                                </Stack>
                            </Paper>

                            <Paper
                                elevation={0}
                                sx={{
                                    borderRadius: 3,
                                    overflow: "hidden",
                                    border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                                    boxShadow: (t) => `0 4px 24px ${alpha(t.palette.common.black, 0.06)}`,
                                }}
                            >
                                <TabContext value={mainTab}>
                                    <Box sx={{ borderBottom: 1, borderColor: "divider", bgcolor: (t) => alpha(t.palette.secondary.main, 0.04) }}>
                                        <TabList onChange={handleMainTab} sx={{ px: 1 }}>
                                            <Tab label="Overview" value="1" sx={{ fontWeight: 700, textTransform: "none" }} />
                                            <Tab label="Students" value="2" sx={{ fontWeight: 700, textTransform: "none" }} />
                                        </TabList>
                                    </Box>

                                    <TabPanel value="1" sx={{ p: { xs: 2, sm: 3 } }}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6} md={6}>
                                                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                                        Periods per week
                                                    </Typography>
                                                    <Typography variant="h6" fontWeight={800}>
                                                        {subjectDetails?.sessions ?? "—"}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                                                        Timetable load / attendance planning
                                                    </Typography>
                                                </Paper>
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={6}>
                                                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                                        Assigned teacher
                                                    </Typography>
                                                    {teacher ? (
                                                        <Typography variant="h6" fontWeight={800} noWrap title={teacher.name}>
                                                            {teacher.name}
                                                        </Typography>
                                                    ) : (
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            startIcon={<PersonAddAlt1OutlinedIcon />}
                                                            sx={{ mt: 1 }}
                                                            onClick={() =>
                                                                navigate(`/Admin/staff/add/${subjectDetails._id}`, {
                                                                    state: { staffRole: "Teacher" },
                                                                })
                                                            }
                                                            disabled={!subjectDetails?._id}
                                                        >
                                                            Assign teacher
                                                        </Button>
                                                    )}
                                                </Paper>
                                            </Grid>
                                        </Grid>
                                    </TabPanel>

                                    <TabPanel value="2" sx={{ p: 0 }}>
                                        <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2, pb: 1 }}>
                                            <Stack
                                                direction={{ xs: "column", sm: "row" }}
                                                spacing={2}
                                                alignItems={{ xs: "stretch", sm: "center" }}
                                                justifyContent="space-between"
                                            >
                                                <ToggleButtonGroup
                                                    exclusive
                                                    value={studentMode}
                                                    onChange={(_, v) => v && setStudentMode(v)}
                                                    size="small"
                                                    color="secondary"
                                                >
                                                    <ToggleButton value="attendance">
                                                        <EventAvailableOutlinedIcon sx={{ mr: 0.75, fontSize: 18 }} />
                                                        Attendance
                                                    </ToggleButton>
                                                    <ToggleButton value="marks">
                                                        <GradingOutlinedIcon sx={{ mr: 0.75, fontSize: 18 }} />
                                                        Marks
                                                    </ToggleButton>
                                                </ToggleButtonGroup>
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<PersonAddAlt1OutlinedIcon />}
                                                    onClick={() => navigate("/Admin/class/addstudents/" + classID)}
                                                >
                                                    Add students to class
                                                </Button>
                                            </Stack>
                                        </Box>
                                        <Divider />

                                        {getresponse ? (
                                            <Box sx={{ py: 6, px: 2, textAlign: "center" }}>
                                                <PersonOutlinedIcon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
                                                <Typography variant="h6" fontWeight={700} gutterBottom>
                                                    No students in this class
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 400, mx: "auto" }}>
                                                    {typeof getresponse === "string" ? getresponse : "Enrol students for this class to take attendance and enter marks."}
                                                </Typography>
                                                <Button variant="contained" onClick={() => navigate("/Admin/class/addstudents/" + classID)}>
                                                    Add students
                                                </Button>
                                            </Box>
                                        ) : studentRows.length === 0 ? (
                                            <Box sx={{ py: 6, px: 2, textAlign: "center" }}>
                                                <PersonOutlinedIcon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
                                                <Typography variant="h6" fontWeight={700} gutterBottom>
                                                    No students yet
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 400, mx: "auto" }}>
                                                    This class has no enrolled students. Add students to record attendance and marks for this subject.
                                                </Typography>
                                                <Button variant="contained" onClick={() => navigate("/Admin/class/addstudents/" + classID)}>
                                                    Add students
                                                </Button>
                                            </Box>
                                        ) : (
                                            <>
                                                <TableContainer>
                                                    <Table size="small">
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell sx={{ fontWeight: 700 }}>Roll</TableCell>
                                                                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                                                                <TableCell align="right" sx={{ fontWeight: 700 }}>
                                                                    Actions
                                                                </TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {paginatedRows.map((row) => (
                                                                <TableRow key={row.id} hover>
                                                                    <TableCell>{row.rollNum}</TableCell>
                                                                    <TableCell>
                                                                        <Typography fontWeight={600}>{row.name}</Typography>
                                                                    </TableCell>
                                                                    <TableCell align="right">
                                                                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                                            <Tooltip title="Student profile">
                                                                                <IconButton
                                                                                    size="small"
                                                                                    color="primary"
                                                                                    onClick={() => navigate("/Admin/students/student/" + row.id)}
                                                                                    sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.08) }}
                                                                                >
                                                                                    <VisibilityOutlinedIcon fontSize="small" />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                            {studentMode === "attendance" ? (
                                                                                <Button
                                                                                    variant="contained"
                                                                                    size="small"
                                                                                    onClick={() =>
                                                                                        navigate(`/Admin/subject/student/attendance/${row.id}/${subjectID}`)
                                                                                    }
                                                                                >
                                                                                    Attendance
                                                                                </Button>
                                                                            ) : (
                                                                                <Button
                                                                                    variant="contained"
                                                                                    size="small"
                                                                                    color="secondary"
                                                                                    onClick={() =>
                                                                                        navigate(`/Admin/subject/student/marks/${row.id}/${subjectID}`)
                                                                                    }
                                                                                >
                                                                                    Marks
                                                                                </Button>
                                                                            )}
                                                                        </Stack>
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                                <TablePagination
                                                    component="div"
                                                    count={studentRows.length}
                                                    page={page}
                                                    onPageChange={(_, p) => setPage(p)}
                                                    rowsPerPage={rowsPerPage}
                                                    onRowsPerPageChange={(e) => {
                                                        setRowsPerPage(parseInt(e.target.value, 10));
                                                        setPage(0);
                                                    }}
                                                    rowsPerPageOptions={[5, 10, 25, 50]}
                                                />
                                            </>
                                        )}
                                    </TabPanel>
                                </TabContext>
                            </Paper>
                        </Stack>
                    </Fade>
                )}
            </Container>
        </Box>
    );
};

export default ViewSubject;
